import { isValidElement, useState, type ReactNode } from 'react';
import type { Dials, RaceStage, RaceStageKind } from '@signalvote/shared';
import { drawTicks, maxCandidates, placesOf, RaceStageKind as Kind, runoffTermsOf, yearDial } from '@signalvote/shared';
import { Switch } from '@/components/ui/switch';
import { getCampaignTodayString } from '@/lib/calendarUtils';
import { cn } from '@/lib/utils';
import { Blank, BlankSheet, useBlankFocus, type BlankFocus } from './Blank';
import { GoalMath } from './GoalMath';
import {
  aimBuys,
  aimWith,
  assumedField,
  countdownWords,
  CUSHIONS,
  cushionLabel,
  cushionWord,
  DRAW_HELP,
  DRAW_PHRASE,
  drawWord,
  dayWords,
  daysUntil,
  fmt,
  HEATS,
  heatWord,
  lastResultWords,
  NUDGES,
  nudgeWord,
  onDrawTick,
  onePlace,
  ownYear,
  pct,
  peopleWord,
  runoffBar,
  stageName,
  winTail,
  yearWord,
  type ReadyWork,
} from './goalWords';
import { openingComplete, openingWords, type RaceOpening } from './RaceOpening';
import { emptyDraft, reseated, workOut, type StageDraft } from './stageGoal';

export interface VoteGoalCalculatorProps {
  /** The state, the Race and the primary, as blanks the sentence opens with. */
  opening: RaceOpening;
  /** The stages the campaign runs in, in date order: its primary, if any, then the general. */
  stages: RaceStage[];
  /** Keyed by stage kind; a stage with no draft starts from our numbers. */
  drafts: Partial<Record<RaceStageKind, StageDraft>>;
  onDraft: (kind: RaceStageKind, draft: StageDraft) => void;
  /** The candidate wrote the Race in, so its numbers were always going to be theirs. */
  writtenIn?: boolean;
}

const firstSentenceClassName = 'font-medium tracking-[-0.02em] text-foreground/75 [text-wrap:pretty] text-[clamp(1.6rem,1.1rem+1.6vw,2.4rem)] leading-[1.6]';

/**
 * The Vote Goal read as sentences with blanks, one per stage, the way the voter list builder reads a list. The first opens
 * with the race's own blanks, and reads only that until the race is set and its primary answered. Simple by default;
 * Advanced adds the year, the cushion and the draw, heat and nudge dials. Every number comes from the shared vote goal math
 * through `workOut`.
 */
export function VoteGoalCalculator(props: VoteGoalCalculatorProps) {
  const [advanced, setAdvanced] = useState(false);
  const [mathOpen, setMathOpen] = useState<Partial<Record<RaceStageKind, boolean>>>({});
  const focus = useBlankFocus();
  const today = getCampaignTodayString();

  return (
    <>
      {!openingComplete(props.opening) || props.stages.length === 0 ? (
        <h2 data-testid="race-sentence" className={firstSentenceClassName}>
          {keepPunctuationAttached([openingWords(props.opening, null, focus), '.'])}
        </h2>
      ) : (
        <div className="space-y-10">
          <div className="flex justify-end">
            <label className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Switch
                checked={advanced}
                onCheckedChange={(on) => {
                  setAdvanced(on);
                  setMathOpen({});
                }}
                aria-label="Advanced"
              />
              Advanced
            </label>
          </div>
          {props.stages.map((stage, i) => (
            <StageGoal
              key={stage.kind}
              {...props}
              stage={stage}
              index={i}
              draft={props.drafts[stage.kind] ?? emptyDraft()}
              advanced={advanced}
              today={today}
              mathOpen={mathOpen[stage.kind] ?? advanced}
              onMath={(isOpen) => setMathOpen((m) => ({ ...m, [stage.kind]: isOpen }))}
              focus={focus}
            />
          ))}
        </div>
      )}
      {/* After every blank, so it draws what they drew this render. */}
      <BlankSheet focus={focus} />
    </>
  );
}

interface StageGoalProps extends VoteGoalCalculatorProps {
  stage: RaceStage;
  index: number;
  draft: StageDraft;
  advanced: boolean;
  today: string;
  mathOpen: boolean;
  onMath: (open: boolean) => void;
  focus: BlankFocus;
}

function StageGoal(props: StageGoalProps) {
  const { stage, index, draft, advanced, onDraft } = props;
  const work = workOut(stage, draft);
  const first = index === 0;
  const setDraft = (next: StageDraft) => onDraft(stage.kind, next);
  const setDials = (patch: Partial<Dials>) => setDraft({ ...draft, dials: { ...draft.dials, ...patch } });
  const blank = (key: string, label: ReactNode, content: (close: () => void) => ReactNode, aria: string) => (
    <Blank key={key} id={`${key}:${stage.kind}`} label={label} aria={aria} focus={props.focus}>
      {content}
    </Blank>
  );

  const opening: ReactNode[] = first
    ? openingWords(props.opening, stage.kind, props.focus)
    : [`Then, in the ${stageName(stage.kind)}`, stage.date ? ` on ${dayWords(stage.date)}` : ''];

  const fieldClause = (w: ReadyWork) => [
    ' with ',
    blank('field', peopleWord(w.dials.candidates), () => <FieldPicker stage={stage} work={w} onCandidates={(candidates) => setDials({ candidates })} />, 'How many are running'),
    w.dials.candidates <= 1 ? ' running' : ' running, including me',
  ];
  const aimText = (w: ReadyWork) => (
    <b key="aim" className={cn('font-bold tabular-nums text-foreground', w.typedAim !== null && 'underline decoration-dotted underline-offset-4')}>{fmt(w.aim)}</b>
  );

  let body: ReactNode[];
  if (stage.status === 'Refused') {
    const raceWord = stage.kind === Kind.General ? 'race' : 'primary';
    const seatsUnknown = stage.rule.kind === 'VoteForSeveral' && stage.rule.seats === null;
    const seats = stage.rule.kind === 'VoteForSeveral' && stage.rule.seats !== null ? ` for one of ${stage.rule.seats} seats` : '';
    const lead = props.writtenIn ? '' : first ? 'We won’t guess this one. ' : 'we won’t guess. ';
    const manualInput = (field: 'votesLastTime' | 'seats', label: string, placeholder: string) => (
      <InlineNumber
        key={field}
        label={label}
        invalid={!!work.errors[field]}
        value={draft.manual[field]}
        placeholder={placeholder}
        onChange={(value) => {
          const next = { ...draft, manual: { ...draft.manual, [field]: value } };
          setDraft(field === 'seats' ? reseated(next) : next);
        }}
      />
    );
    body = [
      seats,
      first ? '. ' : ', ',
      lead,
      'Last time, ',
      manualInput('votesLastTime', `Votes cast in this ${raceWord} last time`, '____'),
      ` votes were cast in this ${raceWord}`,
      ...(seatsUnknown ? ['. Next time, ', manualInput('seats', 'Seats up next time', '__'), ' seats are up'] : []),
      ...(work.kind === 'Ready' ? [', so', ...fieldClause(work), withList(work), ', aim for ', aimText(work), ` votes${winTail(work.contest.rule)}.`] : ['.']),
    ];
  } else {
    const w = work as ReadyWork;
    const seats = stage.rule.kind === 'VoteForSeveral' ? ` for one of ${placesOf(stage.rule)} seats` : '';
    const showYear = advanced || w.dials.year !== w.defaults.year || ownYear(w);
    body = [
      seats,
      ',',
      ...fieldClause(w),
      ...(showYear ? [', in a ', blank('year', yearWord(w), (close) => <YearPicker work={w} advanced={advanced} onYear={(year) => setDials({ year })} close={close} />, 'What kind of year'), ' year'] : []),
      withList(w),
      first ? '. Aim for ' : ', aim for ',
      aimText(w),
      ` votes${winTail(w.contest.rule)}.`,
    ];
  }

  // ", with [a little extra for safety (3 points)], [a ballot measure above mine on the ballot] and [a lot of interest in my
  // race]". Simple shows a clause only when its dial is off our number, so nothing changes the aim out of sight.
  function withList(w: ReadyWork): ReactNode {
    const off = (dial: 'cushion' | 'draw' | 'heat' | 'nudge') => Math.abs(w.dials[dial] - w.defaults[dial]) > 0.001;
    const items: ReactNode[] = [];
    if (advanced || off('cushion'))
      items.push(blank('cushion', cushionWord(w.dials.cushion), (close) => <Options options={CUSHIONS.map((c) => ({ on: Math.abs(w.dials.cushion - c.cushion) < 0.05, label: cushionLabel(c.cushion, onePlace(w)), help: c.help, aim: aimWith(w, { cushion: c.cushion }), pick: () => setDials({ cushion: c.cushion }) }))} heading="How much extra for safety?" help="Votes past the bare win, in case turnout or the count surprises you." close={close} />, 'Extra for safety'));
    if (!w.contest.manual && (advanced || off('draw')))
      items.push(blank('draw', drawWord(w), (close) => <Options heading="What’s above your race on the ballot?" help="Big races above yours bring more voters out than the year alone." close={close} options={drawTicks(w.contest.cycle).map((t) => ({ on: onDrawTick(t, w.dials.draw), label: DRAW_PHRASE[t.id], help: DRAW_HELP[t.id], aim: aimWith(w, { draw: t.points }), pick: () => setDials({ draw: t.points }) }))} />, 'What’s above your race on the ballot'));
    if (advanced || off('heat'))
      items.push(blank('heat', heatWord(w), (close) => <Options heading="How much interest is there in your race?" help="The less voters care, the more of them skip your race on the ballot." close={close} options={HEATS.map((h) => ({ on: heatWord(w) === h.label, label: h.label, help: h.help, aim: aimWith(w, { heat: h.heat }), pick: () => setDials({ heat: h.heat }) }))} />, 'How much interest'));
    if (advanced || off('nudge'))
      items.push(blank('nudge', nudgeWord(w.dials.nudge), (close) => <Options heading="More or fewer voters than we estimate?" help="Move our turnout estimate up or down a little, if you know something we don’t." close={close} options={NUDGES.map((n) => ({ on: Math.abs(w.dials.nudge - n) < 0.05, label: nudgeWord(n), help: n === 0 ? 'Our number.' : '', aim: aimWith(w, { nudge: n }), pick: () => setDials({ nudge: n }) }))} />, 'More or fewer voters than we estimate'));
    if (!items.length) return null;
    return [', with ', ...items.flatMap((item, i) => [i === 0 ? '' : i === items.length - 1 ? ' and ' : ', ', item])];
  }

  const Heading = first ? 'h2' : 'p';
  const runoff = work.kind === 'Ready' ? work.goal.runoff : null;
  const runoffTerms = runoffTermsOf(stage.rule);
  const last = lastResultWords(stage);
  const lastTotal = stage.lastResult?.totalVotes;

  return (
    <section className={cn('space-y-3', !first && 'border-t pt-10')}>
      <Heading
        data-testid={`goal-sentence-${stage.kind}`}
        className={first ? firstSentenceClassName : 'font-medium tracking-[-0.02em] text-foreground/75 [text-wrap:pretty] text-[clamp(1.35rem,1rem+1.2vw,2rem)] leading-[1.6]'}
      >
        {keepPunctuationAttached([opening, body])}
      </Heading>
      {stage.status === 'Refused' && <p className="text-sm text-muted-foreground">{stage.reason}</p>}

      {work.kind === 'Ready' ? (
        <StageNotes stage={stage} work={work} advanced={advanced} />
      ) : (
        <>
          {lastTotal !== undefined && draft.manual.votesLastTime.trim() === '' && (
            <button
              type="button"
              className="rounded-md border border-primary/40 px-3 py-1 text-sm font-semibold text-primary hover:bg-primary/5"
              onClick={() => setDraft({ ...draft, manual: { ...draft.manual, votesLastTime: String(lastTotal) } })}
            >
              Use {fmt(lastTotal)} from last time
            </button>
          )}
          <p className="text-sm text-muted-foreground">
            {work.errors.votesLastTime ?? work.errors.seats ?? work.errors.registered ?? 'Type the number and the rest of the sentence opens. No numbers to go on? A similar nearby race is a fair stand-in.'}
          </p>
        </>
      )}

      {work.kind === 'Ready' && (
        <>
          <button type="button" className="text-sm font-semibold text-primary underline-offset-2 hover:underline" aria-expanded={props.mathOpen} onClick={() => props.onMath(!props.mathOpen)}>
            {props.mathOpen ? 'Hide the math' : 'Show the math'}
          </button>
          {props.mathOpen && <GoalMath stage={stage} draft={draft} work={work} advanced={advanced} onDraft={setDraft} />}
        </>
      )}
      {last && <p className="text-sm text-muted-foreground">{last}</p>}
      {first && stage.date && (
        <p className="text-sm text-muted-foreground">
          {stage.kind === Kind.General ? 'Election' : 'Primary'} day: {dayWords(stage.date)}, {countdownWords(daysUntil(stage.date, props.today))}
        </p>
      )}

      {runoff && runoffTerms && (
        <p data-testid="runoff-sentence" className="mt-6 border-l-4 border-muted pl-4 text-[clamp(1.2rem,0.95rem+1vw,1.75rem)] font-medium leading-[1.6] text-foreground/75">
          If nobody gets more than {runoffBar(stage.rule)}, the top two meet again{runoffTerms.date ? ` on ${dayWords(runoffTerms.date)}` : ' later'}: make the top two with{' '}
          <b className="font-bold tabular-nums text-foreground">{fmt(runoff.toMakeIt)}</b>, then aim for <b className="font-bold tabular-nums text-foreground">{fmt(runoff.aim)}</b>.{' '}
          <span className="block text-base text-muted-foreground">Fewer people vote in a runoff, so {fmt(runoff.aim)} should win it.</span>
        </p>
      )}
    </section>
  );
}

function StageNotes({ stage, work, advanced }: { stage: RaceStage; work: ReadyWork; advanced: boolean }) {
  const atBusy = !work.contest.manual && work.dials.year === work.contest.numbers.turnout.busy;
  const { rule } = work.contest;
  const assumed = assumedField(stage, work);
  // A majority rule asks for half whatever the field; only these hold at half because of how a big field splits.
  const holdsAtHalf = (rule.kind === 'MostVotes' || rule.kind === 'ShareThreshold') && work.dials.candidates >= 3 && work.goal.share === 0.5;
  return (
    <div className="space-y-1 text-sm">
      {work.typedAim !== null ? (
        <p>That’s your own goal. The math says {fmt(work.goal.aim)}.</p>
      ) : (
        work.goal.share > 0 &&
        atBusy && (
          <p>
            We plan for a busy year, when more people vote, so this covers you. In a usual year, about <b className="tabular-nums">{fmt(work.goal.usualAim)}</b> votes would {aimBuys(rule)}.
          </p>
        )
      )}
      {holdsAtHalf && (
        <p className="text-muted-foreground">With {work.dials.candidates} running, we still plan for half the votes, because two strong candidates often take most of the vote.</p>
      )}
      {assumed && <p className="text-muted-foreground">{assumed}</p>}
      {advanced && (
        <p className="text-muted-foreground">
          That’s <b className="text-foreground">{pct(work.goal.aim / Math.max(1, work.goal.votes))}</b> of about <b className="text-foreground">{fmt(work.goal.votes)}</b> votes in this race, from{' '}
          {fmt(work.goal.ballots)} ballots.
          {work.goal.outside === 'High' && ' That’s well past the busiest year we expect.'}
          {work.goal.outside === 'Low' && ' That’s well below the quietest year we expect.'}
        </p>
      )}
    </div>
  );
}

interface Option {
  on: boolean;
  label: string;
  help?: string;
  aim: number;
  pick: () => void;
}

function Options({ heading, help, options, close }: { heading: string; help?: string; options: Option[]; close: () => void }) {
  return (
    <div className="space-y-3">
      <div>
        <h3 className="text-base font-semibold">{heading}</h3>
        {help && <p className="text-sm text-muted-foreground">{help}</p>}
      </div>
      <div className="space-y-1">
        {options.map((o) => (
          <button
            key={o.label}
            type="button"
            aria-pressed={o.on}
            onClick={() => {
              o.pick();
              close();
            }}
            className={cn('flex w-full items-center justify-between gap-3 rounded-md border px-3 py-2 text-left text-sm hover:bg-muted', o.on && 'border-primary bg-primary/5')}
          >
            <span>
              <span className="block font-medium first-letter:uppercase">{o.label}</span>
              {o.help && <span className="block text-muted-foreground">{o.help}</span>}
            </span>
            <span className="whitespace-nowrap tabular-nums text-muted-foreground">{fmt(o.aim)} votes</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function FieldPicker({ stage, work, onCandidates }: { stage: RaceStage; work: ReadyWork; onCandidates: (n: number) => void }) {
  const n = work.dials.candidates;
  // As many running as places means nobody can lose, and the goal falls to one vote.
  const fewest = (placesOf(work.contest.rule) ?? 1) + 1;
  const stepClass = 'flex size-9 items-center justify-center rounded-md border text-lg disabled:opacity-40';
  return (
    <div className="space-y-3">
      <div>
        <h3 className="text-base font-semibold">How many are running?</h3>
        <p className="text-sm text-muted-foreground">
          {stage.kind === Kind.General ? 'Count everyone on the November ballot, independents too, you included.' : 'Count everyone in this primary, you included.'}
        </p>
      </div>
      <div className="flex items-center gap-3">
        <button type="button" className={stepClass} aria-label="One fewer" disabled={n <= fewest} onClick={() => onCandidates(n - 1)}>
          −
        </button>
        <span className="w-6 text-center text-lg font-semibold tabular-nums">{n}</span>
        <button type="button" className={stepClass} aria-label="One more" disabled={n >= maxCandidates(work.contest)} onClick={() => onCandidates(n + 1)}>
          +
        </button>
        <span className="text-sm text-muted-foreground">running · aim for {fmt(work.goal.aim)}</span>
      </div>
      {stage.lastResult && <p className="text-sm text-muted-foreground">Last time, {stage.lastResult.candidates} ran.</p>}
    </div>
  );
}

function YearPicker({ work, advanced, onYear, close }: { work: ReadyWork; advanced: boolean; onYear: (year: number) => void; close: () => void }) {
  if (ownYear(work)) {
    return (
      <p className="text-sm text-muted-foreground">
        You typed {fmt(work.contest.numbers.turnout.usual)} ballots in the math, so the year stays on your number. Use ours from the math.
      </p>
    );
  }
  const { turnout } = work.contest.numbers;
  const windows = [
    { label: 'a quiet year', ballots: turnout.quiet, help: 'Fewer people turn out than usual.' },
    { label: 'a usual year', ballots: turnout.usual, help: 'About as many as most years.' },
    { label: 'a busy year', ballots: turnout.busy, help: 'More people vote than usual. We plan for this one, so the goal covers you.' },
  ];
  const past = advanced ? yearDial(work.contest).anchors.flatMap((a) => (a.kind === 'Past' ? [a] : [])) : [];
  const option = (label: string, ballots: number, help: string): Option => ({
    on: work.dials.year === ballots,
    label,
    help: `${help} About ${fmt(ballots)} people vote.`,
    aim: aimWith(work, { year: ballots }),
    pick: () => onYear(ballots),
  });
  return (
    <Options
      heading="What kind of year?"
      help="How many people vote changes how many votes it takes to win."
      close={close}
      options={[...windows.map((w) => option(w.label, w.ballots, w.help)), ...past.map((p) => option(`like ${p.year}`, p.ballots, 'A year like one we’ve had.'))]}
    />
  );
}

/** A line can break right after a blank, which would start the next line with the comma or full stop that follows it. */
function keepPunctuationAttached(nodes: ReactNode[]): ReactNode[] {
  const out: ReactNode[] = [];
  for (const node of (nodes as unknown[]).flat(Infinity) as ReactNode[]) {
    if (node === '') continue;
    const prev = out[out.length - 1];
    const mark = typeof node === 'string' ? /^[,.;:]/.exec(node)?.[0] : undefined;
    if (mark && isValidElement(prev)) {
      out[out.length - 1] = (
        <span key={`glued-${out.length}`} className="whitespace-nowrap">
          {prev}
          {mark}
        </span>
      );
      out.push((node as string).slice(mark.length));
    } else out.push(node);
  }
  return out;
}

function InlineNumber(props: { label: string; value: string; placeholder: string; invalid: boolean; onChange: (value: string) => void }) {
  return (
    <input
      type="text"
      inputMode="numeric"
      autoComplete="off"
      aria-label={props.label}
      aria-invalid={props.invalid || undefined}
      value={props.value}
      placeholder={props.placeholder}
      onChange={(e) => props.onChange(e.target.value)}
      style={{ width: `${Math.max(props.value.length, props.placeholder.length) * 0.62 + 0.8}em` }}
      className="mx-1 rounded-md border border-dashed border-primary/50 bg-primary/5 px-1 text-center font-semibold text-primary placeholder:text-primary/40 focus:border-solid focus:outline-none aria-[invalid=true]:border-destructive"
    />
  );
}
